import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../../config";
import axios from "axios";

export function useDeleteAllItemOffers() {
    const queryClient = useQueryClient();
    
    return useMutation({
        mutationFn: async ({ itemId }) => {
            const res = await api.delete(`/promotions/items/${itemId}/offers`);
            return res.data;
        },
        onSuccess: (data, variables) => {
            // Invalidar cache relacionado ao item
            queryClient.invalidateQueries(['item-campaigns', variables.itemId]);
            queryClient.invalidateQueries(['offer-information']);
        }
    });
}

export function useManageSellerExclusionStatus() {
    const queryClient = useQueryClient();
    
    return useMutation({
        mutationFn: async ({ data }) => {
            const res = await api.post(`/promotions/exclusion-list/seller`, data);
            return res.data;
        },
        onSuccess: () => {
            // Invalidar cache do status de exclusão do vendedor
            queryClient.invalidateQueries(['seller-exclusion-status']);
        }
    });
}

export function useManageItemExclusionStatus() {
    const queryClient = useQueryClient();
    
    return useMutation({
        mutationFn: async ({ data }) => {
            const res = await api.post(`/promotions/exclusion-list/items`, data);
            return res.data;
        },
        onSuccess: (data, variables) => {
            // Invalidar cache do status de exclusão dos itens
            queryClient.invalidateQueries(['item-exclusion-status']);
        }
    });
}

export function useBulkAddItemsToCampaign() {
    const queryClient = useQueryClient();
    
    return useMutation({
        mutationFn: async ({ promotionId, data }) => {
            const res = await api.post(`/promotions/campaigns/${promotionId}/items/bulk-add`, data);
            return res.data;
        },
        onSuccess: (data, variables) => {
            // Invalidar cache dos itens da campanha
            queryClient.invalidateQueries(['campaign-items', variables.promotionId]);
            queryClient.invalidateQueries(['campaign-details', variables.promotionId]);
        }
    });
}

export function useBulkAddItemsFromDatabase() {
    const queryClient = useQueryClient();
    
    return useMutation({
        mutationFn: async ({ promotionId, data }) => {
            const res = await api.post(`/promotions/campaigns/${promotionId}/items/bulk-add-from-database`, data);
            return res.data;
        },
        onSuccess: (data, variables) => {
            // Invalidar cache dos itens da campanha
            queryClient.invalidateQueries(['campaign-items', variables.promotionId]);
            queryClient.invalidateQueries(['campaign-details', variables.promotionId]);
        }
    });
}

export function useBulkRemoveItemsFromCampaign() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ promotionId, data }) => {
            const res = await api.post(`/promotions/campaigns/${promotionId}/items/bulk-remove`, data);
            return res.data;
        },
        onSuccess: (data, variables) => {
            // Invalidar cache dos itens da campanha
            queryClient.invalidateQueries(['campaign-items', variables.promotionId]);
            queryClient.invalidateQueries(['campaign-details', variables.promotionId]);
        }
    });
}

export function useProcessAllCandidatesWithSuggestedPrices() {
    const queryClient = useQueryClient();
    
    return useMutation({
        mutationFn: async ({ campaignId, data }) => {
            const res = await api.post(`/promotions/campaigns/${campaignId}/candidates/process-all`, data);
            return res.data;
        },
        onSuccess: (data, variables) => {
            // Invalidar cache dos candidatos e da campanha
            queryClient.invalidateQueries(['campaign-candidates', variables.campaignId]);
            queryClient.invalidateQueries(['campaign-details', variables.campaignId]);
            queryClient.invalidateQueries(['campaign-items', variables.campaignId]);
        }
    });
}
export function useCampaignImportCandidates() {
  return useMutation({
    mutationFn: async ({ campaign }) => {
     const response = await api.get(
        `/promotions/campaigns/${campaign.id}/candidates/export-csv?promotion_type=${campaign.type}`,
            {
                responseType: "blob", // necessário para download
            }
        );
        return response.data;
    },
  });
}

export function useBulkJoinCampaigns() {
    const queryClient = useQueryClient();
    
    return useMutation({
        mutationFn: async ({ campaignRequests }) => {
            const results = [];
            
            for (const request of campaignRequests) {
                // 'remove' sai da campanha (bulk-remove), qualquer outro valor entra (bulk-add)
                const action = request.action === 'remove' ? 'remove' : 'add';
                const payload = {
                    ...(request.promotionType ? { promotion_type: request.promotionType } : {}),
                    items: request.items
                };

                try {
                    const res = await api.post(`/promotions/campaigns/${request.campaignId}/items/bulk-${action}`, payload);
                    results.push({
                        campaignId: request.campaignId,
                        action,
                        success: true,
                        data: res.data,
                        itemCount: request.items.length
                    });
                } catch (error) {
                    results.push({
                        campaignId: request.campaignId,
                        action,
                        success: false,
                        error: error.response?.data?.message || error.message,
                        itemCount: request.items.length
                    });
                }
            }
            
            return results;
        },
        onSuccess: (results) => {
            results.forEach(result => {
                if (result.success) {
                    queryClient.invalidateQueries(['campaign-items', result.campaignId]);
                    queryClient.invalidateQueries(['campaign-details', result.campaignId]);
                }
            });
            queryClient.invalidateQueries(['seller-campaigns']);
        }
    });
}

export function useExportCampaignCandidates() {
    return useMutation({
        mutationFn: async ({ data }) => {
            const res = await api.post('/promotions/campaigns/all/candidates/export', data);
            return res.data;
        }
    });
}

export function useCheckExportStatus() {
    return useMutation({
        mutationFn: async ({ jobId }) => {
            const res = await api.get(`/promotions/exports/status/${jobId}`);
            return res.data;
        }
    });
}

export function useDownloadExport() {
    return useMutation({
        mutationFn: async ({ jobId }) => {
            const res = await api.get(`/promotions/exports/download/${jobId}`, {
                responseType: 'blob'
            });
            return res.data;
        }
    });
}
